/**
 * Ortsseiten der 24-Stunden-Betreuung: `/24h-betreuung/<slug>`.
 *
 * Nur für die 24-Stunden-Betreuung — dort wohnt die Betreuungskraft vor Ort,
 * die Entfernung zum Büro in Gifhorn spielt für die Familie keine Rolle. Die
 * Hauswirtschaft (eigene Mitarbeiter fahren hin, 20 km) bekommt keine
 * Ortsseiten; dort entscheidet das Google-Unternehmensprofil.
 *
 * **Gestuft, nicht alles auf einmal.** Elf gleichartige Ortsseiten auf einer
 * Domain mit gut zwanzig indexierten Seiten wertet Google als Doorway Pages
 * ab. Deshalb zuerst Wolfsburg als Vorlage (125.000 Einwohner, 25 km, und für
 * „24 stunden pflege wolfsburg" stand die Seite schon auf Position 35), in der
 * Auswertung messen, dann erweitern. Jede Seite braucht eigenen Text, keine
 * Ortsnamen-Austauschvorlage: Die Texte liegen in `content/ortsseiten.ts`
 * unter `ort.<slug>.…` und sind dort je Ort geschrieben, nicht generiert.
 *
 * Stand 21.09.2026: Wolfsburg ist seit dem 17.09. im Index und steht für
 * „24 stunden pflege wolfsburg" bei Position 9,7 — die Bedingung für die
 * nächste Seite ist damit erfüllt, Braunschweig ist dazugekommen. Celle und
 * Peine erst, wenn auch Braunschweig in der Auswertung Einblendungen zeigt
 * (Fokus-Block, Zeile `/24h-betreuung/braunschweig`). Hannover bewusst nicht:
 * Rand des Radius, und dort werben bundesweite Vermittler.
 *
 * Eine neue Ortsseite braucht: einen Eintrag hier, die Texte in
 * `content/ortsseiten.ts`, den Pfad in `sites.json` (Sitemap und Prerendering)
 * und einen Eintrag in `areaServed` in `public/index.html`.
 *
 * Grundlage der Liste: Fragebogen D1 „Einsatzgebiet" (ops/KUNDENFRAGEN-RELAUNCH.md),
 * vom Kunden ausgefüllt am 28.08.2026.
 */
export interface Ort {
  /** Pfadsegment: `/24h-betreuung/<slug>`. */
  slug: string;
  /** Name, wie er in Überschriften und im Titel steht. */
  name: string;
  /** Entfernung vom Büro in Gifhorn in Kilometern, gerundet. */
  km: number;
  /** Vorschaubild für Social-Netzwerke — solange es kein eigenes gibt, das der 24h-Seite. */
  image: string;
  /** Stadtteile bzw. Ortsteile, die auf der Seite genannt werden. */
  ortsteile: string[];
}

export const ORTE: Ort[] = [
  {
    slug: 'wolfsburg',
    name: 'Wolfsburg',
    km: 25,
    image: '/website/img/24h-hero.jpg',
    ortsteile: [
      'Fallersleben', 'Vorsfelde', 'Detmerode', 'Westhagen', 'Ehmen', 'Mörse',
      'Sülfeld', 'Hehlingen', 'Neuhaus', 'Reislingen', 'Nordsteimke', 'Hattorf',
      'Heiligendorf', 'Wendschott', 'Brackstedt', 'Kästorf',
    ],
  },
  {
    slug: 'braunschweig',
    name: 'Braunschweig',
    km: 30,
    image: '/website/img/24h-hero.jpg',
    ortsteile: [
      'Innenstadt', 'Östliches Ringgebiet', 'Westliches Ringgebiet', 'Nordstadt',
      'Wenden', 'Bienrode', 'Waggum', 'Querum', 'Gliesmarode', 'Riddagshausen',
      'Volkmarode', 'Lehndorf', 'Weststadt', 'Stöckheim', 'Heidberg', 'Mascherode',
    ],
  },
];

/** Orte des Einsatzgebiets der 24-Stunden-Betreuung — auch die ohne eigene Seite. */
export const EINSATZGEBIET_24H = [
  'Gifhorn', 'Wolfsburg', 'Braunschweig', 'Celle', 'Peine', 'Wittingen',
  'Meine', 'Uetze', 'Wendeburg', 'Salzgitter', 'Wolfenbüttel', 'Hannover',
];

export const ortBySlug = (slug: string | undefined): Ort | undefined =>
  ORTE.find((ort) => ort.slug === slug);

export const ortLink = (ort: Ort): string => `/24h-betreuung/${ort.slug}`;
